import {AxiosRequestConfig, AxiosResponse} from 'axios';
import {ENDPOINTS} from 'constants/endpoints';
import {instanceGusaBackend} from 'libs/api';

import {dataSegmentationInstance} from 'libs/rolloutConfiguration/dataSegmentation/dataSegmentation';
import {cookieStore, localStore} from 'libs/storage/storage';
import {IMPACT_RADIUS_COOKIE_KEY, OAUTH_COOKIE_KEY} from 'constants/app';
import {OAuthTokenType} from 'reducers/oAuth/oAuthType';
import {EMAIL, LOGIN_METHOD} from 'constants/login';
import {REAL_IP} from 'constants/cookie';
import {getStoreId} from 'constants/store';

/**
 * Callback for successful response
 * it sets token to local storage if request was on getting token
 * it passes headers to dataSegmentationInstance
 * @param {AxiosResponse<any>} response - response
 * @returns {AxiosResponse<any>} response
 */
export const onFulfilledResponse = (response: AxiosResponse<any>): AxiosResponse<any> => {
    if (response.config.url && response.config.url.includes(ENDPOINTS.CUSTOMER_TOKEN)) {
        cookieStore.set(OAUTH_COOKIE_KEY, response.data);
    }

    if (!cookieStore.get(REAL_IP) && response.headers['x-real-ip']) {
        cookieStore.set(REAL_IP, response.headers['x-real-ip']);
    }

    if (!dataSegmentationInstance.isDataFulfilled()) {
        dataSegmentationInstance.collectData(response.headers);
    }
    return response;
};

/**
 * Callback for succesful response
 * @param {any} error - response
 * @returns {Promise<any>} response
 */
export const onErrorResponse = (error: any): Promise<any> => {
    return Promise.reject(error);
};

/**
 * For all requests adds Authorization field in header with token value
 * from local storage (if exists), but, as token has expire time, if time expires,
 * token needs to be refreshed, it takes place in saga, that is why CustomEvent is dispatched
 * (it was made in saga b/c we need refresh token logic also for an action fetchSelf_REJECTED)
 * @param {AxiosRequestConfig} response - Response
 * @returns {AxiosRequestConfig} Response
 */
export const setAuthorizationHeader = (response: AxiosRequestConfig): AxiosRequestConfig => {
    const tokenData: OAuthTokenType = cookieStore.get(OAUTH_COOKIE_KEY, {});

    if (tokenData.token_type && tokenData.access_token) {
        if (tokenData.expiresDate && tokenData.expiresDate >= Date.now()) {
            // @ts-ignore
            response.headers.Authorization = `${tokenData.token_type} ${tokenData.access_token}`;
        } else {
            window.dispatchEvent(new CustomEvent('oAuth:refreshToken'));
        }
    }

    return response;
};
/**
 * For all requests adds Store in header with store id value
 * we want web view to have the same store id as native app
 * that is why it has ios store id by default
 * but if we have android cookie, we set android store id
 * @param {AxiosRequestConfig} request - request
 * @returns {AxiosRequestConfig} request
 */
export const setStoreHeader = (request: AxiosRequestConfig): AxiosRequestConfig => {
    const storeId = getStoreId();

    // @ts-ignore
    request.headers.store = storeId;
    // @ts-ignore
    request.headers['X-Store-Id'] = storeId;

    return request;
};

/**
 * For all requests adds xImpactRadiusClickId field in header with what we have in corresponding cookie (if exists)
 * @param {AxiosRequestConfig} request - axios request
 * @returns {AxiosRequestConfig} edited request
 */
export const setImpactRadiusHeader = (request: AxiosRequestConfig): AxiosRequestConfig => {
    const impactRadiusData = cookieStore.get(IMPACT_RADIUS_COOKIE_KEY, '');

    if (impactRadiusData && request.headers) {
        request.headers['x-impact-click-id'] = impactRadiusData;
    }

    return request;
};

/**
 * For logout request delete token data from localStorage
 * @param {AxiosRequestConfig} response - Response
 * @returns {AxiosRequestConfig} Response
 */
export const deleteStorageTokenOnLogout = (response: AxiosRequestConfig): AxiosRequestConfig => {
    const {url} = response;
    if (url && url.includes(ENDPOINTS.CUSTOMER_LOGOUT)) {
        cookieStore.remove(OAUTH_COOKIE_KEY);
        localStore.remove(LOGIN_METHOD);
        localStore.remove(EMAIL);
    }

    return response;
};

/**
 *
 */
// eslint-disable-next-line max-statements
export const setImpactRadiusCookie = (request: AxiosRequestConfig): AxiosRequestConfig => {
    const impactRadiusCookie = cookieStore.get(IMPACT_RADIUS_COOKIE_KEY);

    if (cookieStore.get(impactRadiusCookie)) {
        return request;
    }

    if (!request.params?.irclickid && !request.url?.split('?')[1]) {
        return request;
    }

    const queryKeys = request.url
        ?.split('?')[1]
        .split('&')
        .map(entry => entry.split('=')[0]);
    const queryKeyIncludesUtm = queryKeys?.find(item => item.toLowerCase().includes('utm_'));

    if (request.params?.irclickid || queryKeyIncludesUtm) {
        cookieStore.set(
            IMPACT_RADIUS_COOKIE_KEY,
            `https://${process.env.NGINX_DOMAIN}/${request.url}${
                request.headers?.Referrer ? `|${request.headers?.Referrer}` : ''
            }`,
        );
    }
    return request;
};

instanceGusaBackend.interceptors.request.use(setImpactRadiusCookie);
instanceGusaBackend.interceptors.request.use(setAuthorizationHeader);
instanceGusaBackend.interceptors.request.use(setStoreHeader);
instanceGusaBackend.interceptors.request.use(deleteStorageTokenOnLogout);
instanceGusaBackend.interceptors.request.use(setImpactRadiusHeader);
instanceGusaBackend.interceptors.response.use(onFulfilledResponse, onErrorResponse);
